import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import RiskManagement from '@/components/RiskManagement';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, FolderOpen, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

const RiskManagementPage = ({ user, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectsOverview, setProjectsOverview] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(searchParams.get('project') || '');
  const [selectedProject, setSelectedProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects with risk info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, usersRes] = await Promise.all([
          axios.get(`${API}/risk-management/projects-overview`),
          axios.get(`${API}/users`)
        ]);
        setProjectsOverview(overviewRes.data);
        setUsers(usersRes.data);
        
        // If project ID in URL, select it
        if (searchParams.get('project')) {
          const project = overviewRes.data.find(p => p.id === searchParams.get('project'));
          if (project) {
            setSelectedProject(project);
            setSelectedProjectId(project.id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  // Handle project selection
  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    const project = projectsOverview.find(p => p.id === projectId);
    setSelectedProject(project);
    setSearchParams({ project: projectId });
  };

  // Refresh project overview after import
  const refreshProjectOverview = async () => {
    try {
      const overviewRes = await axios.get(`${API}/risk-management/projects-overview`);
      setProjectsOverview(overviewRes.data);
      if (selectedProjectId) {
        const project = overviewRes.data.find(p => p.id === selectedProjectId);
        setSelectedProject(project);
      }
    } catch (error) {
      console.error('Failed to refresh:', error);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Risikomanagement
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwalten und überwachen Sie Projektrisiken
            </p>
          </div>
          
          {/* Project Selector with Risk Info */}
          <div className="w-full sm:w-[350px]">
            <Select value={selectedProjectId} onValueChange={handleProjectChange}>
              <SelectTrigger>
                <SelectValue placeholder="Projekt auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {projectsOverview.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2 w-full">
                      <FolderOpen className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 truncate">{project.name}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Business Case Risk Indicator */}
                        {project.has_bc_risks && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-1.5 py-0 ${
                              project.imported_from_bc 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}
                          >
                            <FileText className="w-3 h-3 mr-0.5" />
                            BC: {project.bc_risk_count}
                            {project.imported_from_bc && <CheckCircle2 className="w-3 h-3 ml-0.5" />}
                          </Badge>
                        )}
                        {/* Total Risks */}
                        {project.total_risks > 0 && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-1.5 py-0 ${
                              project.critical_high_risks > 0 
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                            }`}
                          >
                            {project.critical_high_risks > 0 && <AlertCircle className="w-3 h-3 mr-0.5" />}
                            {project.total_risks}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1 py-0">
                  <FileText className="w-3 h-3" /> BC
                </Badge>
                = Business Case Risiken
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-green-500" />
                = Importiert
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        {!selectedProjectId ? (
          <Card className="p-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Kein Projekt ausgewählt</h2>
            <p className="text-muted-foreground">
              Bitte wählen Sie ein Projekt aus, um dessen Risiken zu verwalten.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Projekte mit <Badge variant="outline" className="bg-blue-100 text-blue-700 px-1 py-0 mx-1"><FileText className="w-3 h-3 inline" /> BC</Badge> 
              haben Risiken im Business Case, die importiert werden können.
            </p>
          </Card>
        ) : (
          <RiskManagement 
            projectId={selectedProjectId}
            projectName={selectedProject?.name}
            users={users}
            onImportComplete={refreshProjectOverview}
            projectHasBcRisks={selectedProject?.has_bc_risks}
            bcRiskCount={selectedProject?.bc_risk_count}
            alreadyImported={selectedProject?.imported_from_bc}
          />
        )}
      </div>
    </Layout>
  );
};

export default RiskManagementPage;
